import type {
  DesktopPraxaIdentityBridge,
  PraxaDesktopIdentitySnapshot,
  PraxaDesktopSignInResult,
} from "@praxa/platform-contracts";
import { useCallback, useEffect, useRef, useState } from "react";

export const PRAXA_IDENTITY_LOAD_ERROR =
  "Praxa account status couldn't load. Local coding is still available.";

export const PRAXA_IDENTITY_ACTION_ERRORS = {
  beginSignIn: "Praxa sign-in couldn't start. Local coding is still available.",
  cancelSignIn: "Praxa sign-in couldn't be cancelled. Local coding is still available.",
  refresh: "Praxa couldn't reconnect. Local coding is still available.",
  resetLocalSession: "The Praxa session couldn't be reset. Local coding is still available.",
  selectWorkspace: "The Praxa workspace couldn't be changed. Local coding is still available.",
  signOut: "Praxa couldn't sign out. Local coding is still available.",
} as const;

export type PraxaIdentityAction = keyof typeof PRAXA_IDENTITY_ACTION_ERRORS;

type PraxaIdentityActionResult = PraxaDesktopIdentitySnapshot | PraxaDesktopSignInResult;

export interface SettledPraxaIdentityAction {
  readonly error: string | null;
  readonly snapshot: PraxaDesktopIdentitySnapshot | null;
}

export interface LoadedPraxaIdentity {
  readonly error: string | null;
  readonly snapshot: PraxaDesktopIdentitySnapshot | null;
}

function getBridge(): DesktopPraxaIdentityBridge | undefined {
  return typeof window === "undefined" ? undefined : window.desktopBridge?.praxaIdentity;
}

export function isDesktopPraxaIdentityAvailable(): boolean {
  return getBridge() !== undefined;
}

function snapshotFromActionResult(result: PraxaIdentityActionResult): PraxaDesktopIdentitySnapshot {
  return "snapshot" in result ? result.snapshot : result;
}

export async function loadPraxaIdentity(
  bridge: Pick<DesktopPraxaIdentityBridge, "getState">,
): Promise<LoadedPraxaIdentity> {
  try {
    return {
      error: null,
      snapshot: await bridge.getState(),
    };
  } catch {
    return {
      error: PRAXA_IDENTITY_LOAD_ERROR,
      snapshot: null,
    };
  }
}

export async function settlePraxaIdentityAction(
  actionName: PraxaIdentityAction,
  action: () => Promise<PraxaIdentityActionResult>,
): Promise<SettledPraxaIdentityAction> {
  try {
    return {
      error: null,
      snapshot: snapshotFromActionResult(await action()),
    };
  } catch {
    return {
      error: PRAXA_IDENTITY_ACTION_ERRORS[actionName],
      snapshot: null,
    };
  }
}

export function useDesktopPraxaIdentity(): {
  readonly applySnapshot: (snapshot: PraxaDesktopIdentitySnapshot) => void;
  readonly bridge: DesktopPraxaIdentityBridge | undefined;
  readonly loadError: string | null;
  readonly loading: boolean;
  readonly retry: () => Promise<void>;
  readonly state: PraxaDesktopIdentitySnapshot | null;
} {
  const [state, setState] = useState<PraxaDesktopIdentitySnapshot | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const stateEventVersionRef = useRef(0);
  const bridge = getBridge();

  const applySnapshot = useCallback((snapshot: PraxaDesktopIdentitySnapshot) => {
    setState(snapshot);
    setLoadError(null);
  }, []);

  const retry = useCallback(async () => {
    if (!bridge) return;

    const stateEventVersion = stateEventVersionRef.current;
    setLoading(true);
    setLoadError(null);
    const result = await loadPraxaIdentity(bridge);
    if (stateEventVersionRef.current === stateEventVersion) {
      if (result.snapshot) {
        applySnapshot(result.snapshot);
      } else {
        setLoadError(result.error);
      }
    }
    setLoading(false);
  }, [applySnapshot, bridge]);

  useEffect(() => {
    if (!bridge) {
      setState(null);
      setLoadError(null);
      setLoading(false);
      return;
    }

    let active = true;
    let unsubscribe = () => {};
    try {
      unsubscribe = bridge.onStateChange((nextState) => {
        stateEventVersionRef.current += 1;
        if (active) applySnapshot(nextState);
      });
    } catch {
      setLoadError(PRAXA_IDENTITY_LOAD_ERROR);
    }
    void retry();
    return () => {
      active = false;
      unsubscribe();
    };
  }, [applySnapshot, bridge, retry]);

  return {
    applySnapshot,
    bridge,
    loadError,
    loading,
    retry,
    state,
  };
}
